import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  MUSCLE_GROUPS, muscleGroupLabel, EXERCISE_TYPES, exerciseTypeLabel, CARDIO_METRICS, cardioMetricLabel,
} from '../../utils/exerciseUtils';
import ConfirmModal from '../../components/ConfirmModal';

const EXERCISE_MEDIA_BUCKET = 'exercise-media';

function randomId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function mediaUrl(path) {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return supabase.storage.from(EXERCISE_MEDIA_BUCKET).getPublicUrl(path).data.publicUrl;
}

// Upload naar de exercise-media-bucket. `folderId` is het oefening-id bij
// bewerken, of een losse sessie-id bij een nieuwe oefening (het id bestaat
// dan nog niet; alleen het pad wordt opgeslagen).
async function uploadMedia(folderId, file) {
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
  const path = `${folderId}/${randomId()}.${ext}`;
  const { error } = await supabase.storage
    .from(EXERCISE_MEDIA_BUCKET)
    .upload(path, file, { contentType: file.type });
  if (error) throw error;
  return path;
}

// Wees-bestand-cleanup: uploads gaan per ontwerp vóór de database-actie
// (storage valt buiten de SQL-transactie), dus wat deze sessie is geüpload
// wordt bij een mislukte opslag weer verwijderd — zelfde patroon als de
// receptenfotogalerij.
async function removeMedia(paths) {
  const eigen = (paths || []).filter(p => p && !p.startsWith('http'));
  if (eigen.length === 0) return;
  await supabase.storage.from(EXERCISE_MEDIA_BUCKET).remove(eigen);
}

// "1 sjabloon" / "3 sjablonen · 2 sessies" — leeg als de oefening nergens
// wordt gebruikt.
function gebruikTekst(gebruik) {
  const delen = [];
  if (gebruik.templates === 1) delen.push(t('exercises.gebruikSjabloon'));
  else if (gebruik.templates > 1) delen.push(t('exercises.gebruikSjablonen', { aantal: gebruik.templates }));
  if (gebruik.sessions === 1) delen.push(t('exercises.gebruikSessie'));
  else if (gebruik.sessions > 1) delen.push(t('exercises.gebruikSessies', { aantal: gebruik.sessions }));
  return delen.join(' · ');
}

function OefeningModal({ oefening, coachId, alleenLezen, onClose, onOpgeslagen }) {
  const isNieuw = !oefening;
  const [naam, setNaam] = useState(oefening?.name ?? '');
  const [spiergroep, setSpiergroep] = useState(oefening?.muscle_group ?? MUSCLE_GROUPS[0]);
  const [type, setType] = useState(oefening?.exercise_type ?? 'kracht');
  const [cardioMetrics, setCardioMetrics] = useState(oefening?.cardio_metrics ?? []);
  const [instructie, setInstructie] = useState(oefening?.instructions ?? '');
  const [imagePath, setImagePath] = useState(oefening?.image_url ?? '');
  const [videoPath, setVideoPath] = useState(oefening?.video_url ?? '');
  const [bezig, setBezig] = useState(false);
  const [uploaden, setUploaden] = useState(false);
  const [fout, setFout] = useState('');
  const folderIdRef = useRef(oefening?.id ?? randomId());
  const nieuwGeuploadRef = useRef([]);

  async function handlePickBestand(e, soort) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploaden(true);
    setFout('');
    try {
      const path = await uploadMedia(folderIdRef.current, file);
      nieuwGeuploadRef.current.push(path);
      if (soort === 'image') setImagePath(path);
      else setVideoPath(path);
    } catch (err) {
      setFout(t('exercises.foutUpload') + err.message);
    } finally {
      setUploaden(false);
    }
  }

  function toggleCardioMetric(metric) {
    setCardioMetrics(prev => (prev.includes(metric) ? prev.filter(m => m !== metric) : [...prev, metric]));
  }

  function handleVerwijderMedia(soort) {
    const path = soort === 'image' ? imagePath : videoPath;
    if (path && nieuwGeuploadRef.current.includes(path)) {
      removeMedia([path]).catch(() => {});
      nieuwGeuploadRef.current = nieuwGeuploadRef.current.filter(p => p !== path);
    }
    if (soort === 'image') setImagePath('');
    else setVideoPath('');
  }

  async function handleOpslaan() {
    if (!naam.trim()) { setFout(t('exercises.foutNaamVerplicht')); return; }
    if (!spiergroep) { setFout(t('exercises.foutSpiergroepVerplicht')); return; }
    if (type === 'cardio' && cardioMetrics.length === 0) {
      setFout(t('exercises.foutCardioMetricVerplicht'));
      return;
    }

    setBezig(true);
    setFout('');
    try {
      const velden = {
        name: naam.trim(),
        muscle_group: spiergroep,
        exercise_type: type,
        cardio_metrics: type === 'cardio' ? cardioMetrics : null,
        instructions: instructie.trim() || null,
        image_url: imagePath || null,
        video_url: videoPath || null,
      };

      // .select() is hier geen luxe: een INSERT/UPDATE die door RLS geen
      // enkele rij raakt levert géén fout op. Zonder deze controle meldde
      // de portal "opgeslagen" terwijl er niets veranderde (de bug waardoor
      // de afbeelding steeds verdween).
      const query = isNieuw
        ? supabase.from('exercises').insert({ ...velden, created_by: coachId, is_custom: true })
        : supabase.from('exercises').update(velden).eq('id', oefening.id);

      const { data, error } = await query.select('id').maybeSingle();
      if (error) throw error;
      if (!data) throw new Error(t('exercises.foutGeenRechten'));

      // Media die tijdens deze sessie is vervangen en niet meer in gebruik is,
      // alsnog uit de bucket halen.
      const behouden = [imagePath, videoPath].filter(Boolean);
      const overbodig = nieuwGeuploadRef.current.filter(p => !behouden.includes(p));
      if (overbodig.length) removeMedia(overbodig).catch(() => {});

      onOpgeslagen();
    } catch (err) {
      if (nieuwGeuploadRef.current.length) removeMedia(nieuwGeuploadRef.current).catch(() => {});
      setFout(t('exercises.foutOpslaan') + err.message);
      setBezig(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isNieuw ? t('exercises.modalTitelNieuw') : t('exercises.modalTitelBewerken')}
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {fout && <div className="error-msg" style={{ marginBottom: 16 }}>{fout}</div>}

        {alleenLezen && (
          <p className="text-muted" style={{ marginBottom: 16 }}>
            {oefening?.created_by
              ? t('exercises.andereCoachAlleenLezen')
              : t('exercises.standaardAlleenLezen')}
          </p>
        )}

        <div className="form-group">
          <label className="form-label">{t('exercises.veldNaam')}</label>
          <input
            className="form-input"
            value={naam}
            maxLength={120}
            disabled={alleenLezen}
            onChange={e => setNaam(e.target.value)}
            placeholder={t('exercises.veldNaamPlaceholder')}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('exercises.veldSpiergroep')}</label>
          <select
            className="form-input"
            value={spiergroep}
            disabled={alleenLezen}
            onChange={e => setSpiergroep(e.target.value)}
          >
            {MUSCLE_GROUPS.map(g => (
              <option key={g} value={g}>{muscleGroupLabel(g)}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">{t('exercises.veldType')}</label>
          <select
            className="form-input"
            value={type}
            disabled={alleenLezen}
            onChange={e => setType(e.target.value)}
          >
            {EXERCISE_TYPES.map(ty => (
              <option key={ty} value={ty}>{exerciseTypeLabel(ty)}</option>
            ))}
          </select>
        </div>

        {type === 'cardio' && (
          <div className="form-group">
            <label className="form-label">{t('exercises.veldCardioMetrics')}</label>
            <p className="text-muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 8 }}>
              {t('exercises.veldCardioMetricsHint')}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {CARDIO_METRICS.map(metric => (
                <label key={metric} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: alleenLezen ? 'default' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={cardioMetrics.includes(metric)}
                    disabled={alleenLezen}
                    onChange={() => toggleCardioMetric(metric)}
                    style={{ width: 16, height: 16, cursor: alleenLezen ? 'default' : 'pointer' }}
                  />
                  {cardioMetricLabel(metric)}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">{t('exercises.veldInstructie')}</label>
          <textarea
            className="form-input"
            rows={4}
            maxLength={4000}
            disabled={alleenLezen}
            value={instructie}
            onChange={e => setInstructie(e.target.value)}
            placeholder={t('exercises.veldInstructiePlaceholder')}
            style={{ resize: 'vertical', minHeight: 90 }}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('exercises.veldAfbeelding')}</label>
          {imagePath ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <img
                src={mediaUrl(imagePath)}
                alt=""
                style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--color-border)' }}
              />
              {!alleenLezen && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => handleVerwijderMedia('image')}>
                  {t('exercises.mediaVerwijderen')}
                </button>
              )}
            </div>
          ) : (
            <input
              type="file"
              accept="image/*"
              disabled={alleenLezen || uploaden}
              onChange={e => handlePickBestand(e, 'image')}
              style={{ fontSize: 13, maxWidth: '100%' }}
            />
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('exercises.veldVideo')}</label>
          {videoPath ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <a href={mediaUrl(videoPath)} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                {t('exercises.videoBekijken')}
              </a>
              {!alleenLezen && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => handleVerwijderMedia('video')}>
                  {t('exercises.mediaVerwijderen')}
                </button>
              )}
            </div>
          ) : (
            <input
              type="file"
              accept="video/*"
              disabled={alleenLezen || uploaden}
              onChange={e => handlePickBestand(e, 'video')}
              style={{ fontSize: 13, maxWidth: '100%' }}
            />
          )}
          <p className="text-muted" style={{ marginTop: 6 }}>{t('exercises.mediaToelichting')}</p>
        </div>

        {uploaden && <p className="text-muted">{t('exercises.bezigUploaden')}</p>}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={onClose}>{t('exercises.annuleren')}</button>
          {!alleenLezen && (
            <button className="btn btn-primary" onClick={handleOpslaan} disabled={bezig || uploaden}>
              {bezig ? '…' : t('exercises.opslaan')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OefeningenPage() {
  const { user, role } = useAuth();
  const { showSuccess } = useToast();
  const [oefeningen, setOefeningen] = useState([]);
  const [makers, setMakers] = useState({});
  const [gebruik, setGebruik] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zoek, setZoek] = useState('');
  const [filterSpiergroep, setFilterSpiergroep] = useState('');
  const [filterType, setFilterType] = useState('');
  const [toonVerborgen, setToonVerborgen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [bewerkTarget, setBewerkTarget] = useState(null);
  const [verwijderTarget, setVerwijderTarget] = useState(null);
  const [verwijderBezig, setVerwijderBezig] = useState(false);
  const [uitgeklapt, setUitgeklapt] = useState(null);

  const magStandaardBewerken = ['admin_coach', 'superadmin'].includes(role);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('exercises')
        .select('id, name, muscle_group, exercise_type, cardio_metrics, created_by, is_custom, is_active, instructions, image_url, video_url')
        .order('name');
      if (err) throw err;
      const rijen = data || [];
      setOefeningen(rijen);

      const makerIds = [...new Set(rijen.map(r => r.created_by).filter(Boolean))];
      if (makerIds.length) {
        // coach_basic_profiles is de kolom-veilige route naar stafnamen.
        const { data: profielen } = await supabase
          .from('coach_basic_profiles')
          .select('id, full_name')
          .in('id', makerIds);
        setMakers(Object.fromEntries((profielen || []).map(p => [p.id, p.full_name])));
      } else {
        setMakers({});
      }

      // Geaggregeerde teller via RPC: rechtstreeks tellen op
      // template_exercise_items zou onder RLS te laag uitvallen en de
      // verwijderknop ten onrechte aanbieden.
      const { data: tellers, error: tellerErr } = await supabase.rpc('get_exercise_usage_counts');
      if (tellerErr) throw tellerErr;
      setGebruik(Object.fromEntries((tellers || []).map(r => [
        r.exercise_id,
        { templates: r.template_count ?? 0, sessions: r.session_count ?? 0 },
      ])));
    } catch (err) {
      setError(t('exercises.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  function herkomstLabel(o) {
    if (!o.created_by) return t('exercises.badgeStandaard');
    const naam = makers[o.created_by];
    return naam ? t('exercises.badgeDoorCoach', { coach: naam }) : t('exercises.badgeEigen');
  }

  function magBewerken(o) {
    if (!o.created_by) return magStandaardBewerken;
    return magStandaardBewerken || o.created_by === user?.id;
  }

  function gebruikVan(o) {
    return gebruik[o.id] || { templates: 0, sessions: 0 };
  }

  function inGebruik(o) {
    const g = gebruikVan(o);
    return g.templates > 0 || g.sessions > 0;
  }

  // Verwijderen kan alleen bij een custom-oefening die nergens wordt
  // gebruikt; systeem-oefeningen zijn nooit verwijderbaar (RLS blokkeert
  // dat ook aan de databasekant).
  function verwijderBlokkade(o) {
    if (!o.created_by) return t('exercises.verwijderStandaardNooit');
    if (!magBewerken(o)) return t('exercises.verwijderAlleenEigen');
    if (inGebruik(o)) return t('exercises.verwijderInGebruik', { gebruik: gebruikTekst(gebruikVan(o)) });
    return '';
  }

  function openNieuw() {
    setBewerkTarget(null);
    setModalOpen(true);
  }

  function openBewerken(o) {
    setBewerkTarget(o);
    setModalOpen(true);
  }

  async function handleOpgeslagen() {
    setModalOpen(false);
    setBewerkTarget(null);
    showSuccess(t('exercises.succesOpgeslagen'));
    await load();
  }

  async function handleToggleZichtbaar(o) {
    setError('');
    try {
      const nieuweWaarde = !o.is_active;
      const { data, error: err } = await supabase
        .from('exercises')
        .update({ is_active: nieuweWaarde })
        .eq('id', o.id)
        .select('id')
        .maybeSingle();
      if (err) throw err;
      if (!data) throw new Error(t('exercises.foutGeenRechten'));
      setOefeningen(prev => prev.map(x => (x.id === o.id ? { ...x, is_active: nieuweWaarde } : x)));
      showSuccess(nieuweWaarde ? t('exercises.succesZichtbaar') : t('exercises.succesVerborgen'));
    } catch (err) {
      setError(t('exercises.foutZichtbaarheid') + err.message);
    }
  }

  async function handleVerwijderBevestigd() {
    const doelwit = verwijderTarget;
    setVerwijderBezig(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('exercises')
        .delete()
        .eq('id', doelwit.id)
        .select('id')
        .maybeSingle();
      // 23503 = foreign_key_violation: de oefening blijkt tóch nog ergens
      // gebruikt te worden (ON DELETE RESTRICT is de harde waarborg).
      if (err) {
        throw new Error(err.code === '23503'
          ? t('exercises.verwijderInGebruik', { gebruik: gebruikTekst(gebruikVan(doelwit)) })
          : err.message);
      }
      if (!data) throw new Error(t('exercises.verwijderAlleenEigen'));

      // Media hoort bij de oefening en heeft er zonder de rij geen bestaan meer.
      await removeMedia([doelwit.image_url, doelwit.video_url]).catch(() => {});

      setVerwijderTarget(null);
      showSuccess(t('exercises.succesVerwijderd'));
      await load();
    } catch (err) {
      setError(t('exercises.foutVerwijderen') + err.message);
      setVerwijderTarget(null);
    } finally {
      setVerwijderBezig(false);
    }
  }

  const gefilterd = oefeningen.filter(o => {
    if (!toonVerborgen && !o.is_active) return false;
    if (filterSpiergroep && o.muscle_group !== filterSpiergroep) return false;
    if (filterType && o.exercise_type !== filterType) return false;
    if (zoek && !o.name.toLowerCase().includes(zoek.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('exercises.title')}</h1>
          <p className="page-subtitle">{t('exercises.subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={openNieuw}>
          {t('exercises.nieuw')}
        </button>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="search"
          className="form-input"
          style={{ flex: '1 1 200px', minWidth: 0, maxWidth: 260 }}
          placeholder={t('exercises.zoekPlaceholder')}
          value={zoek}
          onChange={e => setZoek(e.target.value)}
        />
        <select
          className="form-input"
          style={{ flex: '1 1 180px', minWidth: 0, maxWidth: 220 }}
          value={filterSpiergroep}
          onChange={e => setFilterSpiergroep(e.target.value)}
        >
          <option value="">{t('exercises.alleSpiergroepen')}</option>
          {MUSCLE_GROUPS.map(g => (
            <option key={g} value={g}>{muscleGroupLabel(g)}</option>
          ))}
        </select>
        <select
          className="form-input"
          style={{ flex: '1 1 140px', minWidth: 0, maxWidth: 180 }}
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
        >
          <option value="">{t('exercises.alleTypen')}</option>
          {EXERCISE_TYPES.map(ty => (
            <option key={ty} value={ty}>{exerciseTypeLabel(ty)}</option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text-muted)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={toonVerborgen}
            onChange={e => setToonVerborgen(e.target.checked)}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          {t('exercises.toonVerborgen')}
        </label>
        <span className="text-muted">
          {t('exercises.aantalGevonden', { aantal: gefilterd.length })}
        </span>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('exercises.colNaam')}</th>
                  <th>{t('exercises.colSpiergroep')}</th>
                  <th>{t('exercises.colType')}</th>
                  <th>{t('exercises.colHerkomst')}</th>
                  <th>{t('exercises.colMedia')}</th>
                  <th>{t('exercises.colGebruik')}</th>
                  <th style={{ textAlign: 'right' }}>{t('exercises.colActies')}</th>
                </tr>
              </thead>
              <tbody>
                {gefilterd.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">
                        <div style={{ fontSize: 32 }}>🏋️</div>
                        <p>{t('exercises.geenOefeningen')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  gefilterd.flatMap(o => {
                    const open = uitgeklapt === o.id;
                    const blokkade = verwijderBlokkade(o);
                    const gebruikLabel = inGebruik(o) ? gebruikTekst(gebruikVan(o)) : t('exercises.gebruikGeen');
                    const rijen = [(
                      <tr key={o.id} style={o.is_active ? undefined : { opacity: 0.55 }}>
                        <td className="font-medium">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ padding: '2px 6px' }}
                              onClick={() => setUitgeklapt(open ? null : o.id)}
                              aria-expanded={open}
                              title={open ? t('exercises.detailsVerbergen') : t('exercises.detailsTonen')}
                            >
                              {open ? '▾' : '▸'}
                            </button>
                            <span>{o.name}</span>
                            {!o.is_active && (
                              <span className="badge badge-inactive">{t('exercises.badgeVerborgen')}</span>
                            )}
                          </div>
                        </td>
                        <td>{muscleGroupLabel(o.muscle_group)}</td>
                        <td>
                          <span className={`badge ${o.exercise_type === 'cardio' ? 'badge-cardio' : 'badge-kracht'}`}>
                            {exerciseTypeLabel(o.exercise_type)}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${o.created_by ? 'badge-active' : 'badge-inactive'}`}>
                            {herkomstLabel(o)}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {o.image_url ? (
                              <img
                                src={mediaUrl(o.image_url)}
                                alt=""
                                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--color-border)' }}
                              />
                            ) : null}
                            <span className="text-muted" style={{ fontSize: 13 }}>
                              {o.video_url ? t('exercises.mediaVideo') : (o.image_url ? '' : '—')}
                            </span>
                          </div>
                        </td>
                        <td className="text-muted" style={{ fontSize: 13 }}>{gebruikLabel}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => openBewerken(o)}>
                              {magBewerken(o) ? t('exercises.bewerken') : t('exercises.bekijken')}
                            </button>
                            {magBewerken(o) && (
                              <button className="btn btn-ghost btn-sm" onClick={() => handleToggleZichtbaar(o)}>
                                {o.is_active ? t('exercises.verbergen') : t('exercises.tonen')}
                              </button>
                            )}
                            {o.created_by && magBewerken(o) && (
                              <button
                                className="btn btn-ghost btn-sm"
                                disabled={!!blokkade}
                                title={blokkade || undefined}
                                onClick={() => setVerwijderTarget(o)}
                              >
                                {t('exercises.verwijderen')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )];

                    if (open) {
                      rijen.push(
                        <tr key={`${o.id}-detail`}>
                          <td colSpan={7} style={{ background: 'var(--color-bg)' }}>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '4px 0' }}>
                              {o.image_url && (
                                <img
                                  src={mediaUrl(o.image_url)}
                                  alt=""
                                  style={{ width: 160, maxWidth: '100%', borderRadius: 8, border: '1px solid var(--color-border)' }}
                                />
                              )}
                              <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                                {o.exercise_type === 'cardio' && (
                                  <p style={{ fontSize: 13, marginBottom: 8 }}>
                                    <strong>{t('exercises.veldCardioMetrics')}:</strong>{' '}
                                    {(o.cardio_metrics || []).length > 0
                                      ? o.cardio_metrics.map(cardioMetricLabel).join(', ')
                                      : <span className="text-muted">{t('exercises.gebruikGeen')}</span>}
                                  </p>
                                )}
                                <strong style={{ fontSize: 13 }}>{t('exercises.detailInstructie')}</strong>
                                <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, marginTop: 4 }}>
                                  {o.instructions || <span className="text-muted">{t('exercises.geenInstructie')}</span>}
                                </p>
                                {o.video_url && (
                                  <a href={mediaUrl(o.video_url)} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                                    {t('exercises.videoBekijken')}
                                  </a>
                                )}
                                {blokkade && (
                                  <p className="text-muted" style={{ marginTop: 8, fontSize: 13 }}>{blokkade}</p>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    return rijen;
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <OefeningModal
          oefening={bewerkTarget}
          coachId={user?.id}
          alleenLezen={!!bewerkTarget && !magBewerken(bewerkTarget)}
          onClose={() => { setModalOpen(false); setBewerkTarget(null); }}
          onOpgeslagen={handleOpgeslagen}
        />
      )}

      {verwijderTarget && (
        <ConfirmModal
          title={t('exercises.verwijderTitel')}
          body={t('exercises.verwijderTekst', { naam: verwijderTarget.name })}
          confirmLabel={t('exercises.verwijderBevestigen')}
          danger
          confirmDisabled={verwijderBezig}
          onConfirm={handleVerwijderBevestigd}
          onClose={() => setVerwijderTarget(null)}
        />
      )}
    </div>
  );
}
